// Device vocabulary and policy for US-112439 (Device Listing) and US-112437 (Device Details).
// Per ADR-0010 these live inside the feature and are exported via its `index.ts` — never a
// global constants file, and never inlined as raw strings at a call site.
//
// Wire values below are the live backend's, confirmed by the Tracking Mobile probes recorded in
// greenway_mobile/docs/specs/features/devices/device-listing/api-contract.md.

/**
 * Server-calculated device status, as returned in `effectiveStatus` (US-112439 §5).
 *
 * `EXPIRED` is the one value the backend never sends as an `effectiveStatus` — expiry lives on
 * `subscription.status` instead. It is kept here because every UI surface renders such a device
 * as "Expired", and the wire parser folds an expired subscription into it.
 */
export const DEVICE_STATUS = {
  RUNNING: 'running',
  OFF: 'off',
  DISCONNECTED: 'disconnected',
  EXPIRED: 'expired',
  PENDING_QC: 'pending_qc',
  PENDING_CONFIGURATION: 'pending_configuration',
} as const;

export type DeviceStatus = (typeof DEVICE_STATUS)[keyof typeof DEVICE_STATUS];

/** The five statuses the backend actually returns in `effectiveStatus`. */
export const EFFECTIVE_STATUSES = [
  DEVICE_STATUS.RUNNING,
  DEVICE_STATUS.OFF,
  DEVICE_STATUS.DISCONNECTED,
  DEVICE_STATUS.PENDING_QC,
  DEVICE_STATUS.PENDING_CONFIGURATION,
] as const;

/**
 * Default grid order (US-112439 §1): Running -> Off -> Disconnected -> Expired -> Pending QC
 * -> Pending Configuration, then Last Sync descending within each group.
 *
 * **The server owns this order** — its opaque pagination cursor encodes
 * `{statusPriority, lastActiveAt, id}`, so pages arrive pre-sorted and are concatenated as
 * received. This table exists so the rule is asserted in tests and reproducible in the mock;
 * the client never re-sorts a page it was given.
 */
export const DEVICE_STATUS_SORT_ORDER: Record<DeviceStatus, number> = {
  [DEVICE_STATUS.RUNNING]: 0,
  [DEVICE_STATUS.OFF]: 1,
  [DEVICE_STATUS.DISCONNECTED]: 2,
  [DEVICE_STATUS.EXPIRED]: 3,
  [DEVICE_STATUS.PENDING_QC]: 4,
  [DEVICE_STATUS.PENDING_CONFIGURATION]: 5,
};

export const SUBSCRIPTION_STATUS = {
  ACTIVE: 'active',
  EXPIRED: 'expired',
} as const;

export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUS)[keyof typeof SUBSCRIPTION_STATUS];

/**
 * The server's verdict on whether the whole card is interactive (`cardState` on a list row).
 * Preferred over any client-side derivation whenever present.
 */
export const CARD_STATE = {
  ENABLED: 'enabled',
  DISABLED: 'disabled',
} as const;

export type CardState = (typeof CARD_STATE)[keyof typeof CARD_STATE];

/**
 * A power command — either one already queued and awaiting execution, or the one a click on the
 * power icon would queue next (`powerIcon.action`).
 *
 * Responses use snake_case; the `POST .../commands` **request body** uses SCREAMING_SNAKE. The
 * two spellings are kept apart deliberately (see {@link COMMAND_REQUEST_TYPE}).
 */
export const COMMAND_TYPE = {
  TURN_ON: 'turn_on',
  TURN_OFF: 'turn_off',
} as const;

export type CommandType = (typeof COMMAND_TYPE)[keyof typeof COMMAND_TYPE];

/**
 * The Command lifecycle (CONTEXT.md): a remote action is **Queued**, then **Delivered** to the
 * device, then **Executed** — or **Failed**, which is the only terminal state the UI has to
 * announce (US-112437 §9 "the command fails to execute").
 *
 * Kept separate from {@link COMMAND_TYPE}, which is the *direction* of the command, not its state.
 */
export const COMMAND_STATUS = {
  QUEUED: 'queued',
  DELIVERED: 'delivered',
  EXECUTED: 'executed',
  FAILED: 'failed',
} as const;

export type CommandStatus = (typeof COMMAND_STATUS)[keyof typeof COMMAND_STATUS];

/** Wire value for the command request body — SCREAMING_SNAKE, unlike every response field. */
export const COMMAND_REQUEST_TYPE: Record<CommandType, 'TURN_ON' | 'TURN_OFF'> = {
  [COMMAND_TYPE.TURN_ON]: 'TURN_ON',
  [COMMAND_TYPE.TURN_OFF]: 'TURN_OFF',
};

/**
 * Sentinel for the "All" quick-filter tab. The other tabs are the grower's own categories from
 * `GET /devices/tracking/meta/categories`, so they are data, not constants — see
 * `useDeviceCategories`.
 *
 * There is deliberately **no** Diesel/Electric/Sensors tab set: the backend carries no
 * diesel/electric/sensor classification at all. `deviceType` on a list row is a free-form
 * category *name* ("Moisture Sensors", "Flow Controllers", …) that varies per account.
 */
export const ALL_CATEGORIES = 'all' as const;

/**
 * Filter-panel Device Status chips (US-112439 §10).
 *
 * The panel labels the two live states **Active** and **Idle** where the card badge says
 * **Running** and **Off**. That is intentional in the design system, not a slip — Tracking
 * Mobile ships the same split off the same Figma library. Expired has no chip here; it is
 * covered by the Subscription Status group, and has no `deviceStatus` query value.
 */
export const DEVICE_STATUS_FILTERS: { value: DeviceStatus; label: string }[] = [
  { value: DEVICE_STATUS.DISCONNECTED, label: 'Disconnected' },
  { value: DEVICE_STATUS.RUNNING, label: 'Active' },
  { value: DEVICE_STATUS.OFF, label: 'Idle' },
  { value: DEVICE_STATUS.PENDING_QC, label: 'Pending QC' },
  { value: DEVICE_STATUS.PENDING_CONFIGURATION, label: 'Pending Configuration' },
];

export const SUBSCRIPTION_STATUS_FILTERS: { value: SubscriptionStatus; label: string }[] = [
  { value: SUBSCRIPTION_STATUS.ACTIVE, label: 'Active' },
  { value: SUBSCRIPTION_STATUS.EXPIRED, label: 'Expired' },
];

/**
 * The single Action filter chip. The `action` query param accepts only `queued`, so an
 * "unqueued" counterpart is not expressible — selecting nothing means no filter.
 */
export const ACTION_FILTER_QUEUED = 'queued' as const;

export type ActionFilter = typeof ACTION_FILTER_QUEUED;

export const DEVICE_POLICY = {
  /**
   * Infinite-scroll batch size.
   *
   * The AC asks for batches of **18**, but `limit` on `GET /devices/tracking/list` must be a
   * **multiple of 10, minimum 10** — 18 is rejected by the backend. 20 is the nearest legal
   * value and the one Tracking Mobile ships (`kDeviceListPageSize`). Flagged for the BA.
   */
  PAGE_SIZE: 20,
  /** Search input hard cap; further input is prevented (US-112439 §9). */
  SEARCH_MAX_LENGTH: 50,
  /** Debounce for the incremental search so typing doesn't fire a request per keystroke. */
  SEARCH_DEBOUNCE_MS: 250,
  /** Rename Device name bounds (Figma node 475:421684 inline error copy). */
  NAME_MIN_LENGTH: 2,
  NAME_MAX_LENGTH: 50,
} as const;

/** User-facing copy the AC specifies verbatim. */
export const DEVICE_COPY = {
  EMPTY: 'No Devices Found',
  BATCH_ERROR: "Couldn't load more devices. Please try again.",
  QUEUE_SUCCESS: {
    [COMMAND_TYPE.TURN_ON]: 'Turn On command queued successfully',
    [COMMAND_TYPE.TURN_OFF]: 'Turn Off command queued successfully',
  },
  QUEUED_TAG: {
    [COMMAND_TYPE.TURN_ON]: 'Turn on Queued',
    [COMMAND_TYPE.TURN_OFF]: 'Turn off Queued',
  },
  COMMAND_FAILED: "Command couldn't be delivered. Please try again.",
  /** §13 "Device not found" — the device is gone, so the page returns to the listing. */
  NOT_FOUND: 'Device not found. This device may have been removed.',
  /**
   * §12 "the page is not accessible and I receive an access denied message". The AC names the
   * message but not its wording, so this is our copy; the *distinction* from NOT_FOUND is the
   * part the AC is explicit about.
   */
  ACCESS_DENIED: "You don't have access to this device.",
  /** Anything else — a 500, a dropped connection — which is retryable, unlike the two above. */
  LOAD_FAILED: "Couldn't load this device. Please try again.",
} as const;

/**
 * Error codes this feature branches on. 400/403/409 are each overloaded across the devices tag
 * and carry no documented code, so the *call site* is the only reliable discriminator — the
 * command mutations map by HTTP status, not by code.
 */
export const DEVICE_ERROR_STATUS = {
  REJECTED: 400,
  SUBSCRIPTION_EXPIRED: 403,
  /**
   * The same 403, at the details-read call site, where it means the device belongs to another
   * grower (US-112437 §12) rather than a lapsed subscription. Named separately precisely because
   * the status is overloaded and only the call site disambiguates it.
   */
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  ALREADY_QUEUED: 409,
} as const;
